<script lang="ts">
  import CodeBlock from '../CodeBlock.svelte';
  import { formatCount } from '../../lib/complexity/format';
  import { arrayBytes, linearLookupSteps, OPS_PER_SEC, SANDBOX_MEMORY_BYTES } from '../../lib/labs';

  /**
   * ラボ 3 の手順 0（labs.md）: 番号ごとの得点を配列で記録する。番号が大きいと配列が作れず、
   * 名前の組を前から探す・contains で調べると回数が膨れることを数字で見せる。
   */
  interface Props {
    ondone: () => void;
  }

  let { ondone }: Props = $props();

  let maxId: number | null = $state(null);
  let triedBig = $state(false);
  let part = $state(1);

  const N = 200_000;
  const steps = linearLookupSteps(N, N);
  const seconds = Math.round(steps / OPS_PER_SEC);

  function pick(m: number) {
    maxId = m;
    if (m > 1000) triedBig = true;
  }

  const CODE_ARRAY = `// 番号 x の人の得点を score[x] に記録する
var score = Array(repeating: 0, count: maxId + 1)
score[x] = 80
print(score[x])`;
  const CODE_PAIRS = `// 名前と得点の組を前から探す
var pairs: [(name: String, score: Int)] = []
for p in pairs {
    if p.name == name { print(p.score); break }
}`;
  const CODE_CONTAINS = `// この番号は前に出たか
var seenList: [Int] = []
if seenList.contains(x) { print("Yes") }
seenList.append(x)`;
</script>

<section class="panel intro" aria-labelledby="intro-title" data-testid="lab-intro">
  <h2 id="intro-title">まず、配列の添字を番号にして記録してみる</h2>
  <p>
    番号 x の人の得点を記録し、あとで聞かれたら答えます。番号を配列の添字にして、<code
      >score[x]</code
    > に得点を入れる方法です。
  </p>

  <div class="grid">
    <div class="col">
      <p class="small">番号はいくつまでありますか。</p>
      <div class="row" role="group" aria-label="番号の上限">
        <button
          type="button"
          class="btn btn-sm"
          class:btn-primary={maxId === 100}
          aria-pressed={maxId === 100}
          onclick={() => pick(100)}>100 まで</button
        >
        <button
          type="button"
          class="btn btn-sm"
          class:btn-primary={maxId === 1e9}
          aria-pressed={maxId === 1e9}
          onclick={() => pick(1e9)}>10^9 まで</button
        >
      </div>
      {#if maxId !== null}
        {@const bytes = arrayBytes(maxId)}
        <p role="status">
          長さ {maxId === 100 ? '101' : '10^9 + 1'} の配列は、約 {formatCount(bytes)} バイトです。
          {#if bytes <= SANDBOX_MEMORY_BYTES}
            作れます。
          {:else}
            実行環境のメモリの上限（512MiB、約 5.4×10^8 バイト）を超えるので、作れません。
          {/if}
        </p>
      {/if}
    </div>
    <div class="col"><CodeBlock code={CODE_ARRAY} label="配列で記録するコード" /></div>
  </div>

  {#if triedBig}
    <div class="grid step">
      <div class="col">
        <p>
          番号の代わりに名前（文字列）だと、配列の添字にできません。名前と得点の組を配列に入れ、前から探すことはできます。
        </p>
        {#if part >= 2}
          <p role="status">
            N = Q = {formatCount(N)}（記録 N 人、問い合わせ Q 回）なら、見る回数は約
            <strong class="num">{formatCount(steps)}</strong> 回。1 秒に約 10^8 回とすると約 {seconds}
            秒です。
          </p>
        {:else}
          <button type="button" class="btn btn-sm" onclick={() => (part = 2)}
            >N = Q = {formatCount(N)} で数える</button
          >
        {/if}
      </div>
      <div class="col"><CodeBlock code={CODE_PAIRS} label="組を前から探すコード" /></div>
    </div>
  {/if}

  {#if part >= 2}
    <div class="grid step">
      <div class="col">
        <p>
          「この番号は前に出たか」を、配列の <code>contains</code> で毎回調べても、中で前から 1 つずつ見ます。
        </p>
        {#if part >= 3}
          <p role="status">
            同じく約 <strong class="num">{formatCount(steps)}</strong> 回です。
          </p>
        {:else}
          <button type="button" class="btn btn-sm" onclick={() => (part = 3)}
            >N = Q = {formatCount(N)} で数える</button
          >
        {/if}
      </div>
      <div class="col"><CodeBlock code={CODE_CONTAINS} label="contains で調べるコード" /></div>
    </div>
  {/if}

  {#if part >= 3}
    <div class="reveal">
      <p>
        前から順に探さなくても、辞書（<code>Dictionary</code
        >）ならキーから値を直接引けます。出現済みかどうかは
        <code>Set</code> の <code>contains</code> で調べます。どちらも平均 O(1) です。
      </p>
      <button type="button" class="btn btn-primary" onclick={ondone} data-testid="intro-done"
        >辞書と Set で解く</button
      >
    </div>
  {:else if !triedBig}
    <p class="text-soft small">両方の上限を試すと、次へ進めます。</p>
  {/if}
</section>

<style>
  .intro {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .intro h2 {
    margin: 0;
  }
  .intro p {
    margin: 0;
  }
  .grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: start;
  }
  .grid.step {
    padding-top: var(--space-4);
    border-top: 1px solid var(--border);
  }
  .col {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
    align-items: flex-start;
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .small {
    font-size: var(--fs-sm);
  }
  .num {
    font-variant-numeric: tabular-nums;
    font-size: var(--fs-lg);
  }
  .reveal {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    align-items: flex-start;
    padding: var(--space-4);
    border-radius: var(--radius-md);
    background: var(--accent-soft);
  }
  @media (max-width: 1023px) {
    .grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
